import type { CallState } from "@/softphone/softphone-context";

/**
 * The sound test's progress, read off the phone's own call list (ADR-062 in `platform`).
 *
 * The switch rings the rep's phone, plays a prompt and a tone, records what arrives from their
 * microphone and plays it back, then hangs up. None of that is reported to the console on its
 * own: the call is the only signal, so the phase is derived from it rather than tracked beside
 * it — a phase kept in step by hand is how a dialog says "Listening" over a call that ended.
 *
 *   idle        nothing asked for
 *   placing     asked for, and the phone has not rung yet
 *   ringing     the call is in the list and not up yet (the phone answers it by itself)
 *   connected   the call is up: prompt, tone, the rep speaks, playback
 *   ended       the call was seen and has gone
 */
export type SoundTestPhase = "idle" | "placing" | "ringing" | "connected" | "ended";

/**
 * How long a test may sit in `placing` before the dialog says the phone never rang.
 *
 * A switch that cannot reach the phone says so on the bus, not to the browser, so silence is the
 * only answer the console gets. Long enough for a background tab to wake and take the offer.
 */
export const NO_RING_MS = 15_000;

export function soundTestPhase(
  callId: string | null,
  call: { state: CallState } | undefined,
  seen: boolean,
): SoundTestPhase {
  if (!callId) return "idle";
  if (!call) return seen ? "ended" : "placing";
  if (call.state === "active" || call.state === "held") return "connected";
  if (call.state === "ended") return "ended";
  return "ringing";
}
